import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

import useDepollution from "../../data/useDepollution";
import useVehicleSingle from "../../data/useVehicleSingle";
import LoadingWheel from "../../components/shared/LoadingWheel";
import ContactFromId from "../../components/contacts/ContactFromId";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Card from "../../components/shared/Card/Card";
import DepollutionButton from "../../components/depollution/DepollutionButton";

export default function Depollution({ depollutionId: depollutionIdProp, vehicleId, collapsible = false }: { depollutionId?: string; vehicleId?: string; collapsible?: boolean; }) {
  const { depollutionId: depollutionIdParam } = useParams();
  const isStandalonePage = depollutionIdProp === undefined && vehicleId === undefined;

  const { setInfo } = usePageNameContext();
  const { saveHandlers } = useDefaultCRUDHandlers("Depollution item");

  const { vehicle: vehicleQuery } = useVehicleSingle(vehicleId);
  const vehicleDepollutionId = vehicleQuery.data?.data?.depollutionId;
  const resolvedDepollutionId = depollutionIdProp ?? (vehicleId ? vehicleDepollutionId ?? undefined : depollutionIdParam);

  const { depollution, depollutionItems, depollutionSteps, saveDepollutionItem } = useDepollution(resolvedDepollutionId);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingStepId, setSavingStepId] = useState<string | null>(null);

  const steps = depollutionSteps.data?.data ?? [];
  const items = depollutionItems.data?.data ?? [];
  const vehicle = depollution.data?.data?.vehicle;

  useEffect(() => {
    if (!isStandalonePage || depollution.isLoading) return;

    if (!depollution.data?.data) {
      setInfo({
        name: "Depollution not found",
        desc: "The depollution record you are looking for does not exist",
      });
      return;
    }

    setInfo({
      name: `Depollution - ${vehicle?.vrm ?? "Vehicle"}`,
      desc: `${vehicle?.combinedMake ?? ""} ${vehicle?.combinedModel ?? ""}`.trim(),
    });
  }, [isStandalonePage, depollution.isLoading, depollution.data?.data?.id, vehicle?.vrm, vehicle?.combinedMake, vehicle?.combinedModel]);

  useEffect(() => {
    if (!depollutionItems.data?.data) return;

    const nextValues: Record<string, string> = {};
    for (const item of depollutionItems.data.data) {
      nextValues[item.depollutionStepId] = item.value ?? "";
    }
    setDraftValues(nextValues);
  }, [depollutionItems.data?.data]);

  const rowData = useMemo(() => {
    return steps.map((step) => {
      const item = items.find((x) => x.depollutionStepId === step.id);
      return {
        step,
        item,
        isCompleted: !!item?.completed,
      };
    });
  }, [steps, items]);

  const completedCount = rowData.filter((x) => x.isCompleted).length;
  const totalCount = rowData.length;
  const allCompleted = totalCount > 0 && completedCount === totalCount;

  const saveStep = (depollutionStepId: string, isCompleted: boolean) => {
    const vehicleId = depollution.data?.data?.vehicleId;
    if (!vehicleId) return;

    setSavingStepId(depollutionStepId);
    saveDepollutionItem.mutate(
      {
        vehicleId,
        depollutionStepId,
        value: draftValues[depollutionStepId] ?? "",
        isCompleted,
      },
      {
        onSuccess: saveHandlers.onSuccess,
        onError: saveHandlers.onError,
        onSettled: () => setSavingStepId(null),
      }
    );
  };

  const isDepollutionDataLoading = !!resolvedDepollutionId && (depollution.isLoading || depollutionItems.isLoading);
  if ((vehicleId && !depollutionIdProp && vehicleQuery.isLoading) || isDepollutionDataLoading || depollutionSteps.isLoading) {
    return (
      <Card title="Depollution" collapsible={false}>
        <div className="p-4">
          <LoadingWheel />
        </div>
      </Card>
    );
  }

  if (vehicleId && !depollution.data?.data) {
    return (
      <Card title="Depollution" collapsible={false}>
        <div className="p-4">
          <DepollutionButton
            vehicleId={vehicleId}
            depollutionId={vehicleDepollutionId}
            openInNewTab={false}
            navigateOnCreate={false}
          />
        </div>
      </Card>
    );
  }

  if (!depollution.data?.data) return null;

  return (
    <Card
      title={`Depollution ${completedCount}/${totalCount} ${completedCount === totalCount ? "✔" : ""}`}
      collapsible={collapsible}
      defaultOpen={collapsible ? !allCompleted : true}
      titleClassName={collapsible && completedCount === totalCount ? "bg-green-300" : ""}
    >
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Item</th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Value</th>
            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Completed By</th>
            <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rowData.map(({ step, item, isCompleted }) => (
            <tr key={step.id} className="bg-white hover:bg-gray-50">
              <td className="px-4 py-2 text-sm">{step.name}</td>
              <td className="px-4 py-2 text-sm">
                {step.needsValue ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={draftValues[step.id] ?? ""}
                      onChange={(e) =>
                        setDraftValues((prev) => ({ ...prev, [step.id]: e.target.value }))
                      }
                      className="w-full max-w-xs rounded-md border border-gray-300 px-2 py-1 text-sm"
                      placeholder={step.options ? `Options: ${step.options}` : "Enter value"}
                    />
                    <ButtonInput
                      label="Save"
                      isSubmit={false}
                      classes=""
                      isLoading={savingStepId === step.id}
                      onClick={() => saveStep(step.id, isCompleted)}
                    />
                  </div>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-4 py-2 text-sm text-gray-700">
                {item?.completedBy ? <ContactFromId contactId={item.completedBy} /> : <span className="text-gray-400">-</span>}
              </td>
              <td className="px-4 py-2 text-center">
                <button
                  className="inline-flex items-center justify-center"
                  onClick={() => saveStep(step.id, !isCompleted)}
                  disabled={savingStepId === step.id}
                >
                  {isCompleted ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <span className="h-5 w-5 rounded-full border border-gray-300 inline-block" />
                  )}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
